<template>
  <card-container
    style="padding: 20px; padding-bottom: 0; overflow: visible"
    class="tooth-card is-v2-form"
  >
    <div class="loading-container" v-if="isLoading">
      <div class="loading-inner-container">
        <div class="spinner" />
      </div>
    </div>

    <!-- 치아 번호 선택 섹션 (전체 너비) -->
    <div class="teeth-number-section">
      <row-with-label
        :label-title="$t('workorder_form_tooth_number')"
        :is-required="true"
        :has-error="false"
        :is-column="false"
        :marginTopLabel="true"
      >
        <template #hint>
          <card-field-hint field="toothPosition" />
        </template>
        <div class="teeth-number-selector">
          <input
            class="ant-like-input"
            type="text"
            style="border-radius: 6px; width: 100%; margin-bottom: 12px"
            :value="selectedTeethDisplay"
            :placeholder="$t('workorder_form_select_tooth_placeholder')"
            readonly
          />
          <teeth-area
            :isSafari="isSafari"
            :selectedTeethNumbers="localToothNumbers"
            @selectTooth="handleToothSelection"
            :getsFixedHeight="true"
          />
        </div>
      </row-with-label>

      <!-- 선택된 치아 번호 표시 -->
      <div v-if="localToothNumbers.length > 0" class="selected-teeth-display">
        {{ formattedSelectedTeeth }}
      </div>
    </div>

    <!-- 2열 레이아웃: 왼쪽(입력 폼), 오른쪽(인증서 이미지) -->
    <div class="teeth-item-wrap">
      <div class="teeth-contents-area">
        <!-- 시술방식 선택 -->
        <!-- label-width 120px: 产品选择 라벨(4자+필수점+? 아이콘)이 기본 80px 상자를 넘겨
             아이콘이 잘린다(label에 overflow:hidden). RowWithLabel 은 절대 고치지 말 것 —
             힌트 전용 칸을 예약했다가 치아그리드가 좌우 대칭으로 잘리는 회귀를 냈다.
             해법은 힌트를 쓰는 폼에서 label-width 를 넓히는 것(CustomerInfoForm·WorkerInfoForm 선례).
             위 치아번호 행(.teeth-number-section)은 제외한다 — 그 행을 좁히면 치아그리드가 다시 잘린다.
             이 4개 행은 .teeth-contents-area 라는 별도 컨테이너라 정렬 그룹이 다르다. -->
        <row-with-label
          label-width="120px"
          :label-title="$t('workorder_form_treatment_type')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                value="CROWN"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_crown") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="IMPLANT_CROWN"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_implant_crown") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="BRIDGE"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_bridge") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="INLAY"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_inlay") }}</span>
            </label>
            <label class="radio-label">
              <input
                type="radio"
                value="LAMINATE"
                v-model="localTreatmentType"
                @change="handleTreatmentTypeChange"
              />
              <span>{{ $t("workorder_form_laminate") }}</span>
            </label>
          </div>
        </row-with-label>

        <!-- 제조사 컨테이너 -->
        <row-with-label
          label-width="120px"
          :label-title="$t('workorder_form_manufacturer')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div
            style="
              position: relative;
              display: flex;
              flex-direction: column;
              gap: 24px;
              z-index: 4;
            "
          >
            <div class="is-certified-wrap">
              <span class="is-certified-label">
                <div class="left-area">
                  <div class="companies-wrap">
                    <button
                      class="company-chip-wrap"
                      v-for="certifiedBrand in sortedCertifiedCompanies"
                      :key="certifiedBrand.name"
                      :class="
                        localToothInfo.isCertified &&
                        certifiedBrand.name === localToothInfo.group
                          ? 'selected'
                          : ''
                      "
                      :name="`certification${arrayIndex}`"
                      :id="`${certifiedBrand.name}${arrayIndex}`"
                      @click="handleGroupClick(certifiedBrand.name, true)"
                    >
                      <div class="certified-brand">
                        <img :src="certifiedBrand.iconUrl" />
                      </div>
                      <span
                        class="product-company-label"
                        :for="`${certifiedBrand.name}${arrayIndex}`"
                      >
                        {{ getCompanyDisplayName(certifiedBrand.name) }}
                        <i class="fa-solid fa-badge-check" />
                      </span>
                    </button>
                  </div>
                </div>
                <div class="right-area">
                  <button
                    class="change-order-btn"
                    @click="openManufacturerSettingModal"
                  >
                    <i class="fa-regular fa-arrow-right-arrow-left" />
                    {{ $t("workorder_form_change_order") }}
                  </button>
                </div>
              </span>
            </div>
          </div>
        </row-with-label>

        <!-- 상세 선택 -->
        <row-with-label
          label-width="120px"
          :label-title="$t('workorder_form_detail_selection')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <div class="radio-group">
            <label class="radio-label">
              <input
                type="radio"
                value="ZIRCONIA"
                v-model="localToothInfo.detailSelection"
                @change="handleDetailSelectionChange"
              />
              <span>{{ $t("workorder_form_zirconia") }}</span>
            </label>
          </div>
        </row-with-label>

        <!-- 실물카드 검색 (덴탈맥스 선택 시에만 표시) — 카탈로그+수기입력을 실제 발급된 카드 검색으로 대체
             SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-001~005/008 -->
        <row-with-label
          label-width="120px"
          :label-title="$t('workorder_form_product_selection')"
          :is-required="true"
          :has-error="false"
          :is-column="!windowInnerWidthGreaterThan1000"
        >
          <!-- 고른 카드의 식별자 2종(난수·GTIN)이 실물카드 어디에 있는지 안내.
               둘 다 카드지 기인쇄 값이라 작업지시서가 인쇄하지 않는다. -->
          <template #hint>
            <card-field-hint field="cardIdentity" />
          </template>
          <!-- 덴탈맥스: 실물카드 통합 검색(q) — randomNo/gtin/lot 부분일치, 태그형 멀티선택
               (SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 REQ-ADM-MULTI-001~004. 바코드 스캔도
               동일 검색창으로 들어와 같은 select/deselect 경로를 타므로 REQ-ADM-MULTI-003도 이 콤보로 충족) -->
          <div
            v-if="isDentalmaxGroup(localToothInfo.group)"
            class="card-search-wrapper"
          >
            <cert-card-search-select
              mode="multiple"
              :value="selectedRandomNos"
              :placeholder="$t('workorder_form_card_search_placeholder')"
              :option-disabled="cardOptionDisabled"
              :option-badge="cardOptionBadge"
              @select="onCardSearchSelect"
              @deselect="onCardSearchDeselect"
            />
            <!-- 카운터 — 카드 n/N장 (N=이 카드의 선택 치아 수), REQ-ADM-MULTI-002 -->
            <div class="card-counter-line">
              {{
                $t("workorder_card_counter", {
                  n: localToothInfo.products.length,
                  total: cardCap,
                })
              }}
            </div>
            <!-- 환자 자가발급(자가발급 인증서) 역연결 안내 배너 — 필수 안내문구.
                 아래 치아/제품/환자정보가 환자 미니앱 자가발급 값으로 pre-fill되었음을 명시한다. -->
            <a-alert
              v-if="hasSelfIssuedCard"
              type="warning"
              show-icon
              :message="
                $t('workorder_self_issue_banner', { teeth: selfIssueBannerTeeth })
              "
              style="margin-top: 8px"
            />
          </div>

          <!-- 다른 제조사는 기존 input 유지 -->
          <a-input
            v-else
            :value="localToothInfo.itemName"
            :placeholder="$t('workorder_form_product_name_placeholder')"
            readonly
            @click="handleProductClick"
          />
        </row-with-label>
        <!-- STM 표면처리 컨테이너 (덴탈맥스 선택 시에만 표시) -->
        <div
          class="surface-container"
          v-if="isDentalmaxGroup(localToothInfo.group)"
        >
          <div class="surface-title-area">
            <div class="surface-text-button">
              <div class="tooltip-label-wrap">
                <div
                  class="question-mark"
                  @click="modalHandler('stmModal', true)"
                >
                  <i class="fa-solid fa-question" />
                </div>
                <div class="label-wrap">
                  <div class="label-text" style="margin-bottom: 0px">
                    {{ $t("workorder_form_stm_surface_treatment") }}
                  </div>
                </div>
              </div>
              <div class="switch-text-wrap">
                <a-switch
                  v-model:checked="localToothInfo.switches.stm"
                  @change="toggleStmSwitch"
                />
              </div>
            </div>
            <p
              class="true-color"
              :style="
                localToothInfo.switches.stm
                  ? 'color: rgb(39, 138, 236);'
                  : 'color: #ACB2B9;'
              "
            >
              {{
                localToothInfo.switches.stm
                  ? $t("workorder_form_stm_completed")
                  : $t("workorder_form_stm_not_processed")
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- 인증서 이미지 영역 -->
      <div class="certificate-img-wrapper">
        <!-- STM 적용 시 -->
        <div v-if="showStmCertificateImage" class="certificate-img-area">
          <div class="certificate-img full">
            <img :src="companyLogoUrl" />
            <img class="stm-overlay-img" :src="stmCertificateImageUrl" />
          </div>
        </div>
        <!-- 기본 상태 -->
        <div v-else class="certificate-img-area">
          <div
            class="certificate-img full"
            :ref="`certificate-img-${arrayIndex}`"
            :class="isFlipped ? 'flip' : ''"
          >
            <img :src="companyLogoUrl" />
            <div class="card-back"><img :src="companyLogoUrl" /></div>
          </div>
        </div>
      </div>
    </div>
  </card-container>

  <!-- STM 모달 -->
  <stm-modal :showModal="modals.stmModal.show" @closeModal="modalHandler" />
</template>

<script>
import CardContainer from "./CardContainer.vue";
import RowWithLabel from "@/views/components/RowWithLabel";
import CardFieldHint from "./CardFieldHint.vue";
import TeethArea from "./TeethArea.vue";
import StmModal from "./StmModal.vue";
import CertCardSearchSelect from "@/components/CertCardSearchSelect.vue";
import certificateCardApi from "@/apis/certificateCard";
import { isDentalmaxGroup } from "../utils/manufacturerGroup";

export default {
  name: "ZirconiaToothInfoForm",
  components: {
    CardContainer,
    RowWithLabel,
    CardFieldHint,
    TeethArea,
    StmModal,
    CertCardSearchSelect,
  },
  props: {
    toothInfo: {
      type: Object,
      required: true,
    },
    arrayIndex: {
      type: Number,
      required: true,
    },
    windowInnerWidthGreaterThan1000: {
      type: Boolean,
      default: false,
    },
    modalShowHistory: {
      type: Object,
      default: () => ({}),
    },
    certifiedCompanies: {
      type: Array,
      default: () => [],
    },
    notCertifiedCompanies: {
      type: Array,
      default: () => [],
    },
    selectedTeethNumbers: {
      type: Array,
      default: () => [],
    },
    prevTeethNos: {
      type: Array,
      default: () => [],
    },
    // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-004 — 같은(미저장) 작업지시서 내
    // 다른 치아가 이미 선택한 randomNo 목록(DB 배정 전 인-오더 클라이언트 가드용). 자기 자신은 제외하고 전달받는다.
    otherAssignedRandomNos: {
      type: Array,
      default: () => [],
    },
    // index.vue의 detectedSelfIssues({randomNo: baseline{teeth,products}}) — durable(저장 시 diff
    // 기준값). 필수 안내배너·치아-deselect 가드 완화는 이 prop에서 파생한다(로컬 transient 상태 아님).
    // 카드 확정(:key 리마운트)·스텝 이동에도 소실되지 않는다.
    selfIssuedMap: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    "update:tooth-info",
    "update:modal-show-history",
    "update-manufacturer-list",
    // 환자 자가발급(자가발급 인증서) 역연결 감지 시 상위(Step3→index)로 릴레이
    "self-issue-detected",
  ],
  data() {
    const initialToothNumbers = this.toothInfo?.toothNumbers
      ? [...this.toothInfo.toothNumbers]
      : [];

    return {
      isLoading: false,
      isFlipped: false,
      isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
      localToothNumbers: initialToothNumbers,
      localTreatmentType: this.toothInfo?.treatmentType || null,
      localToothInfo: {
        ...this.toothInfo,
        toothNumbers: initialToothNumbers,
        treatmentType: this.toothInfo?.treatmentType || null,
        manufacturerId: "",
        switches: {
          stm: this.toothInfo?.switches?.stm || false,
          ...this.toothInfo?.switches,
        },
        // group 기본값 하드코딩 제거(2026-07-20 SSOT): 비워두면 autoSelectSingleManufacturer가
        // 실제 handleGroupClick 경로로 단일 인증 제조사를 선택해 manufacturerId까지 세팅한다.
        group: this.toothInfo?.group || "",
        isCertified: this.toothInfo?.isCertified !== false,
        itemName: this.toothInfo?.itemName || "",
        detailSelection: this.toothInfo?.detailSelection || "ZIRCONIA",
        // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-003 — 치아 데이터 모델 신설 필드.
        // 선택된 실물카드의 randomNo를 바인딩한다(수기 GTIN/제조일자/LOT 입력 대체). 비-덴탈맥스 하위호환용.
        randomNo: this.toothInfo?.randomNo || "",
        // SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 DEC-009 — 카드(실물카드/제품) 배열.
        // 순서=선택 순서. 덴탈맥스 태그형 멀티선택 전용(비-덴탈맥스는 항상 빈 배열).
        products: Array.isArray(this.toothInfo?.products)
          ? [...this.toothInfo.products]
          : [],
      },
      modals: {
        stmModal: {
          show: false,
        },
        history: {
          stm: false,
        },
      },
      // 카드 애니메이션 중복 방지 플래그
      flipAnimationPending: false,
    };
  },
  computed: {
    sortedCertifiedCompanies() {
      return this.certifiedCompanies.sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
    },
    // @MX:NOTE: [AUTO] 선택 제조사의 cardImageUrl 기반 동적 카드 이미지 — 없으면 기본값 폴백
    // @MX:WARN: [AUTO] zh 로케일 + 덴탈맥스 그룹은 제조사 _cardImageUrl(한국어 인증서일 수 있음)보다
    //   확인된 중국어 인증서 이미지를 우선한다. customer-miniapp pages/dentalmax/certification/my-v2.vue의
    //   defaultImage와 동일 경로로 실사용이 검증된 자산이다(2026-07-14 조사).
    // @MX:REASON: [AUTO] _cardImageUrl은 제조사 마스터데이터(BE) 값으로 로케일 구분이 없어, zh 화면에서도
    //   한국어 인증서 이미지가 그대로 노출되는 버그가 있었다. 덴탈맥스 zh 조합에서만 로케일 자산을 강제한다.
    companyLogoUrl() {
      const isZhDentalmax =
        this.$i18n.locale === "zh" && isDentalmaxGroup(this.localToothInfo.group);
      if (isZhDentalmax) {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese_v2.png";
      }
      // 선택된 제조사가 cardImageUrl을 제공하면 우선 사용
      const cardImageUrl = this.localToothInfo._cardImageUrl;
      if (cardImageUrl) return cardImageUrl;
      // 폴백: locale별 기본 이미지
      if (this.$i18n.locale === "zh") {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/zirconia_chinese_v2.png";
      }
      return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/Zirconia.png";
    },
    // @MX:NOTE: [AUTO] STM 표시는 isCertified(공인 제조사) + stm 스위치 조합으로 판단 — 한국어 이름 문자열 의존 제거
    showStmCertificateImage() {
      return (
        this.localToothInfo.isCertified &&
        this.localToothInfo.switches?.stm
      );
    },
    stmCertificateImageUrl() {
      // STM 이미지는 locale 기준 유지 (제조사별 STM 이미지가 없으므로 폴백 불필요)
      if (this.$i18n.locale === "zh") {
        return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/DENT/stm_chinese.png";
      }
      return "https://certilife.blob.core.windows.net/certilife/project_hardcoding_files/v1/ccafc24ca12c46ebbbabd8b194911349/certilife/nft_card/dentalmax_stm.png";
    },
    selectedTeethDisplay() {
      if (this.localToothNumbers.length === 0) return "";
      return this.localToothNumbers.sort((a, b) => a - b).join(", ");
    },
    // Wave2 REQ-ADM-MULTI-002 — 카드 상한 = 이 카드의 선택 치아 수
    cardCap() {
      return this.localToothNumbers.length;
    },
    // 멀티선택 a-select의 :value(선택된 randomNo 배열), 순서=products 배열 순서(선택 순서)
    selectedRandomNos() {
      return (this.localToothInfo.products || []).map((p) => p.randomNo);
    },
    formattedSelectedTeeth() {
      if (this.localToothNumbers.length === 0) return "";
      const sorted = [...this.localToothNumbers].sort((a, b) => a - b);
      return this.$t("workorder_form_tooth_format", {
        teeth: sorted.join(", "),
      });
    },
    // 이 카드(치아그룹)에 담긴 제품 중 환자 자가발급(selfIssuedMap)과 연결된 첫 항목.
    // durable prop(index.vue의 detectedSelfIssues)에서 파생하므로 카드 확정(:key 리마운트)·
    // 스텝 이동에도 생존한다(로컬 transient 상태였던 selfIssueBaseline 대체).
    selfIssuedCardInfo() {
      const products = this.localToothInfo.products || [];
      const matched = products.find(
        (product) => product.randomNo && this.selfIssuedMap[product.randomNo]
      );
      if (!matched) return null;
      const entry = this.selfIssuedMap[matched.randomNo] || {};
      return {
        randomNo: matched.randomNo,
        teeth: Array.isArray(entry.teeth) ? entry.teeth : [],
      };
    },
    // 필수 안내배너 노출 여부 + 치아-deselect 가드 완화 판정에 공용으로 쓰인다.
    hasSelfIssuedCard() {
      return !!this.selfIssuedCardInfo;
    },
    // 자가발급 안내 배너용 치아 목록(정렬·콤마 표시) — selfIssuedCardInfo.teeth 우선, 없으면 현재 선택 치아.
    selfIssueBannerTeeth() {
      const teeth = this.selfIssuedCardInfo?.teeth?.length
        ? this.selfIssuedCardInfo.teeth
        : this.localToothNumbers;
      if (!teeth?.length) return "";
      return [...teeth].sort((a, b) => a - b).join(", ");
    },
  },
  watch: {
    toothInfo: {
      handler(newVal) {
        if (newVal) {
          this.localToothInfo = {
            ...newVal,
            switches: {
              ...this.localToothInfo.switches,
              ...newVal.switches,
            },
            // Wave2 DEC-009 — products 배열은 얕은 복제로 동기화(부모/자식 배열 참조 공유 방지)
            products: Array.isArray(newVal.products) ? [...newVal.products] : [],
          };

          if (
            newVal.toothNumbers !== undefined &&
            Array.isArray(newVal.toothNumbers)
          ) {
            this.localToothNumbers = [...newVal.toothNumbers];
          }

          if (newVal.treatmentType) {
            this.localTreatmentType = newVal.treatmentType;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    certifiedCompanies: {
      handler(newVal) {
        // 인증 제조사가 변경되었을 때도 자동 선택 시도
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.autoSelectSingleManufacturer();
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // 컴포넌트 마운트 시 제조사 자동 선택
    this.autoSelectSingleManufacturer();
  },
  methods: {
    // CertCardSearchSelect의 optionDisabled prop — used 카드 또는 인-오더 다른 치아가 이미
    // 선택한 카드는 비활성화 (REQ-FE-STEP3-002/004)
    cardOptionDisabled(item) {
      return item.used || this.isRandomNoUsedElsewhere(item.randomNo);
    },

    // CertCardSearchSelect의 optionBadge prop — used가 아닐 때의 화면별 추가 배지
    // (used 자체의 "이미 사용됨" 배지는 컴포넌트가 공통으로 렌더링한다)
    cardOptionBadge(item) {
      if (this.isRandomNoUsedElsewhere(item.randomNo)) {
        return { text: this.$t("workorder_form_card_used_other_tooth"), tone: "danger" };
      }
      if (this.isInCurrentCard(item.randomNo)) {
        return { text: this.$t("workorder_form_card_in_this_group"), tone: "success" };
      }
      return null;
    },

    // 실물카드 태그형 멀티선택 — 선택 시 products[]에 추가(append)한다.
    // (SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 REQ-ADM-MULTI-001/002/003/004)
    // 바코드 스캔도 동일 통합검색창을 거치므로 이 핸들러 하나로 REQ-ADM-MULTI-003(스캔 칩 누적)까지 충족한다.
    // CertCardSearchSelect의 select 이벤트는 정규화된 카드 항목 전체를 전달한다.
    async onCardSearchSelect(selected) {
      if (!selected) return;
      const randomNo = selected.randomNo;

      // 방어적 중복 가드 — 정상 흐름에서는 이미 담긴 항목이 옵션에서 disabled 처리되진 않지만
      // (✓ 배지로만 구분), 재선택 이벤트가 들어오면 조용히 무시하지 않고 안내한다.
      if (this.isInCurrentCard(randomNo)) {
        this.$message.info(this.$t("workorder_card_duplicate"));
        return;
      }

      // used 카드/같은 주문 내 다른 치아가 이미 선택한 카드는 disabled지만, 방어적으로 재확인
      // (REQ-FE-STEP3-002/004)
      if (selected.used) {
        this.$message.warning(
          selected.usedReason || this.$t("workorder_form_card_used")
        );
        return;
      }
      if (this.isRandomNoUsedElsewhere(selected.randomNo)) {
        this.$message.warning(this.$t("workorder_form_card_used_other_tooth"));
        return;
      }

      // 상한 = 이 카드의 선택 치아 수 (REQ-ADM-MULTI-002, 실물카드 1블럭=30장 정합)
      if (this.localToothInfo.products.length >= this.cardCap) {
        this.$message.warning(
          this.$t("workorder_card_limit_reached", { total: this.cardCap })
        );
        return;
      }

      this.localToothInfo.products.push({
        randomNo: selected.randomNo,
        gtinCode: selected.gtin,
        itemName: selected.productName || "",
        lotNumber: selected.inboundBatchNumber || "",
        manufacturingDate: "",
        productCode: selected.gtin,
      });

      this.performFlipEffect();
      this.emitToothInfo();

      // 환자 자가발급 역연결 pre-fill — 덴탈맥스 전용, best-effort(조회 실패는 카드담기 자체를 막지 않음)
      if (isDentalmaxGroup(this.localToothInfo.group)) {
        await this.checkSelfIssuedCert(randomNo);
      }
    },

    // 실물카드 태그 제거(× 클릭 또는 드롭다운 재클릭 토글) — products[]에서 제거.
    // CertCardSearchSelect의 deselect 이벤트는 카드 항목(최소 {randomNo})을 전달한다.
    onCardSearchDeselect(deselected) {
      const randomNo = deselected?.randomNo;
      if (!randomNo) return;
      const index = this.localToothInfo.products.findIndex(
        (product) => product.randomNo === randomNo
      );
      if (index === -1) return;
      this.localToothInfo.products.splice(index, 1);
      // 배너/가드는 selfIssuedMap(prop) + products[] 파생 computed이므로, 해당 제품을 빼면
      // selfIssuedCardInfo가 자연히 null로 재계산된다 — 별도 로컬 리셋 불필요.
      this.emitToothInfo();
    },

    // 선택된 난수(随机码)에 환자 자가발급(미니앱 자가발급) 인증서가 연결되어 있는지 조회하고,
    // 있으면 치아/제품을 pre-fill한다. 자가발급 번들(teeth/products)은 이 카드의 값을 대체하는
    // 권위 있는 값으로 취급한다(REQ: 환자신원 배선 통합 — Step3 자가발급 역연결 pre-fill).
    async checkSelfIssuedCert(randomNo) {
      let response;
      try {
        response = await certificateCardApi.getSelfIssuedCert(randomNo);
      } catch (error) {
        // 조회 실패는 기존 카드담기 흐름을 막지 않는다(best-effort)
        console.error("[자가발급 조회 실패]", randomNo, error);
        return;
      }

      const data = response?.data?.data ?? response?.data ?? {};
      if (!data.selfIssued) return;

      const teeth = Array.isArray(data.teeth) ? [...data.teeth] : [];
      // 자가발급은 "1카드=N치아"를 products N개(치아별, 동일 randomNo)로 저장한다. admin 카드그룹
      // 모델은 카드 1장 = products 1항목(치아셋은 toothNumbers)이므로, pre-fill 시 randomNo 기준으로
      // 중복제거해야 한다 — 안 하면 저장 시 toothDetails가 카드당 N중복이 되어 작업지시서가 오염된다.
      // 수동선택 경로는 isInCurrentCard로 중복을 막지만 pre-fill은 그 게이트를 우회하므로 여기서 처리.
      const seenRandomNo = new Set();
      const products = (Array.isArray(data.products) ? data.products : [])
        .filter((product) => {
          const rn = product.randomNo || randomNo;
          if (seenRandomNo.has(rn)) return false;
          seenRandomNo.add(rn);
          return true;
        })
        .map((product) => ({
          randomNo: product.randomNo || randomNo,
          gtinCode: product.productCode || "",
          itemName: product.itemName || "",
          lotNumber: product.lotNumber || "",
          manufacturingDate: "",
          productCode: product.productCode || "",
        }));

      if (teeth.length > 0) {
        this.localToothNumbers = [...teeth];
        this.localToothInfo.toothNumbers = [...teeth];
      }
      if (products.length > 0) {
        this.localToothInfo.products = products;
      }

      const baseline = {
        teeth: [...teeth],
        products: products.map((product) => ({ ...product })),
      };

      // 배너/가드는 이제 selfIssuedMap(index.vue의 durable detectedSelfIssues) prop에서 파생된다.
      // 이 emit이 index.vue의 상태를 채우면, 부모→prop→여기 computed(selfIssuedCardInfo)로
      // 반응형 재계산되어 배너가 노출된다(카드 확정 리마운트·스텝 이동에도 생존).
      this.emitToothInfo();
      this.$emit("self-issue-detected", {
        randomNo,
        patient: data.patient || null,
        teeth: [...teeth],
        baseline,
      });
    },

    // 같은(미저장) 작업지시서의 다른 치아가 이미 선택한 randomNo인지 확인 (REQ-FE-STEP3-004, 인-오더 가드)
    isRandomNoUsedElsewhere(randomNo) {
      if (!randomNo) return false;
      if (this.isInCurrentCard(randomNo)) return false; // 이 카드 안에서는 "다른 그룹"이 아님
      if (this.localToothInfo.randomNo === randomNo) return false; // 레거시 스칼라 자기 자신 제외(비-덴탈맥스 병행)
      return (this.otherAssignedRandomNos || []).includes(randomNo);
    },

    // 이 카드(치아그룹)에 이미 담긴 randomNo인지 확인 — "이 그룹에 담김 ✓" 배지 (REQ-ADM-MULTI-004)
    isInCurrentCard(randomNo) {
      return (this.localToothInfo.products || []).some(
        (product) => product.randomNo === randomNo
      );
    },

    // 제조사 이름 표시 (중국어일 때 DENTALMAX로 표시)
    getCompanyDisplayName(companyName) {
      if (
        this.$i18n.locale === "zh" &&
        (companyName === "덴탈맥스" || companyName === "DENTALMAX")
      ) {
        return "DENTALMAX";
      }
      return companyName;
    },

    handleToothSelection(toothNumber) {
      if (!this.localToothNumbers) {
        this.localToothNumbers = [];
      }

      const index = this.localToothNumbers.indexOf(toothNumber);
      if (index > -1) {
        // 선택 해제 시도 — 덴탈맥스 카드에 담긴 제품(카드) 수가 해제 후 치아 수를 초과하게 되면 차단.
        // "카드를 먼저 제거해야 치아 해제 가능"이 가장 놀라움이 적은 기본값이다(Wave2 소프트 가드).
        // 단, 환자 자가발급 역연결로 pre-fill된 카드는 이 가드를 완화한다 — 자가발급 신원(치아/제품)이
        // 기공소 입력과 다를 수 있어 편집(추가/해제)이 가능해야 하기 때문(비-자가발급 경로는 기존대로 유지).
        // hasSelfIssuedCard는 selfIssuedMap(prop, durable) 파생 computed — 카드 확정 리마운트에도 생존.
        if (
          isDentalmaxGroup(this.localToothInfo.group) &&
          !this.hasSelfIssuedCard &&
          this.localToothInfo.products.length >= this.localToothNumbers.length
        ) {
          this.$message.warning(this.$t("workorder_teeth_deselect_blocked"));
          return;
        }
        this.localToothNumbers.splice(index, 1);
      } else {
        // 선택 - 치아 30개 초과 소프트 가드 (REQ-ADM-MULTI-005, 실물카드 1블럭=30장 정합)
        if (this.localToothNumbers.length >= 30) {
          this.$message.warning(this.$t("workorder_teeth_limit_reached"));
          return;
        }
        // 중복 선택 허용
        this.localToothNumbers.push(toothNumber);
      }

      this.localToothInfo.toothNumbers = [...this.localToothNumbers];
      this.emitToothInfo();
    },

    handleTreatmentTypeChange() {
      this.localToothInfo.treatmentType = this.localTreatmentType;
      this.emitToothInfo();
    },

    handleDetailSelectionChange() {
      this.emitToothInfo();
    },

    handleGroupClick(groupName, isCertified) {
      // 덴탈맥스는 제품 정체성(itemName/brand)을 여기서 미리 채우지 않는다 — 실제 실물카드를
      // 선택하기 전까지는 비워둬야 hasCardProduct(Step3 SSOT)가 "카드 없음"을 정확히 판정한다.
      // (자동 제조사 1개 선택 시 itemName만으로 필수 입력을 통과시키던 회귀의 원인)
      // @MX:WARN: [AUTO] 이 분기를 제거하면 덴탈맥스도 다시 name-only 카드가 만들어질 수 있다.
      // @MX:REASON: [AUTO] Step3.hasCardProduct/normalizeCardProducts가 group으로 분기하므로
      //   여기서 itemName을 채우면 그쪽 가드와 값이 어긋난다(운영 오염 1건, 2026-08-13).
      const defaultItemName = isDentalmaxGroup(groupName)
        ? ""
        : this.getDefaultItemName(groupName);
      const selectedCompany = this.certifiedCompanies.find(
        (company) => company.name === groupName
      );
      const manufacturerId = selectedCompany?._id || selectedCompany?.id || "";

      this.localToothInfo = {
        ...this.localToothInfo,
        group: groupName,
        isCertified: isCertified,
        customGroup: groupName === "etc" ? "" : groupName,
        itemName: defaultItemName,
        manufacturerId: manufacturerId,
        productInfoWithoutCode: {
          ...this.localToothInfo.productInfoWithoutCode,
          brand: defaultItemName,
        },
        // @MX:NOTE: [AUTO] 카드 미리보기 동적화 — 선택 제조사 시각 정보를 _card* 필드로 보관
        _cardImageUrl: selectedCompany?.cardImageUrl || null,
        _colorCode: selectedCompany?.colorCode || null,
        _nftTitle: selectedCompany?.nftTitle || null,
        _iconUrl: selectedCompany?.iconUrl || null,
      };
      this.emitToothInfo();
      this.performFlipEffect();
    },

    getDefaultItemName(groupName) {
      const selectedCompany = this.certifiedCompanies.find(
        (company) => company.name === groupName
      );
      return selectedCompany?.customFields?.itemName || "";
    },

    handleProductClick() {
      if (this.localToothInfo.itemName) {
        this.performFlipEffect();
      }
    },

    toggleStmSwitch(checked) {
      if (!this.localToothInfo.switches) {
        this.localToothInfo.switches = {};
      }

      this.localToothInfo.switches.stm = checked;

      if (checked && !this.modals.history.stm) {
        this.modalHandler("stmModal", true);
        this.modals.history.stm = true;
      }

      this.emitToothInfo();
    },

    modalHandler(modalType, action) {
      if (!this.modals[modalType]) return;
      this.modals[modalType].show = action;
    },

    openManufacturerSettingModal() {
      // 순서 변경 모달 (필요시 구현)
    },

    performFlipEffect() {
      // 카드 1회전 애니메이션 (flip -> flip back)
      if (this.isFlipped) return; // 이미 애니메이션 중이면 무시

      setTimeout(() => {
        this.isFlipped = true; // 0 -> 180도 회전
        setTimeout(() => {
          this.isFlipped = false; // 180 -> 0도 회전 (원래 위치로)
        }, 600); // 0.6초 후 복귀
      }, 10);
    },

    // 카드 애니메이션 1회만 실행 (중복 방지)
    performFlipEffectOnce() {
      if (this.flipAnimationPending) return;
      this.flipAnimationPending = true;
      setTimeout(() => {
        this.isFlipped = !this.isFlipped;
        // 애니메이션 완료 후 플래그 리셋 (1초 후)
        setTimeout(() => {
          this.flipAnimationPending = false;
        }, 1000);
      }, 10);
    },

    emitToothInfo() {
      const toothInfoToEmit = JSON.parse(JSON.stringify(this.localToothInfo));
      this.$emit("update:tooth-info", toothInfoToEmit);
    },

    // 인증 제조사가 1개만 있을 때 자동 선택
    isDentalmaxGroup,

    autoSelectSingleManufacturer() {
      // 이미 제조사가 선택되어 있으면 스킵
      if (this.localToothInfo.group) {
        return;
      }

      // 인증 제조사가 정확히 1개만 있는지 확인
      if (
        this.sortedCertifiedCompanies &&
        this.sortedCertifiedCompanies.length === 1
      ) {
        const singleCompany = this.sortedCertifiedCompanies[0];
        console.log("[자동 선택] 인증 제조사 1개 감지:", singleCompany.name);

        // 자동으로 선택
        this.handleGroupClick(singleCompany.name, true);
      }
    },
  },
};
</script>
<style lang="scss">
.is-v2-form {
  label.label-column {
    line-height: 1 !important;
    min-height: 40px !important;
    justify-content: center !important;
  }
  input[type="radio"]:checked {
    background-color: #1677ff;
  }
  .ant-radio-wrapper .ant-radio-checked .ant-radio-inner {
    background-color: #1677ff;
    border-color: #1677ff;
  }
  .input-error-wrap {
    line-height: 1 !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }
}

// GTIN 자동완성 드롭다운 옵션 스타일 (unscoped - 드롭다운은 body에 렌더링됨)
.gtin-option-item {
  display: flex;
  flex-direction: column;
  padding: 4px 0;

  .gtin-option-name {
    font-size: 14px;
    font-weight: 500;
    color: #2f3337;
  }

  .gtin-option-code {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
}

// 실물카드 검색 드롭다운 옵션 스타일은 공유 컴포넌트로 이전됨
// (src/components/CertCardSearchSelect.vue, SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 M2 origin)
</style>
<style scoped lang="scss">
// 치아 번호 선택 섹션
.teeth-number-section {
  width: 100%;
  margin-bottom: 24px;

  .teeth-number-selector {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .selected-teeth-display {
    margin-top: 24px;
    font-size: 15px;
    font-weight: 600;
    text-align: left;
    color: #2f3337;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: "SUIT Variable";
    font-size: 17px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
  }
}

// 라디오 버튼 그룹 스타일
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .radio-label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    input[type="radio"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    span {
      font-size: 15px;
      color: #2f3337;
    }
  }
}

// 2열 레이아웃
.teeth-item-wrap {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.teeth-contents-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.certificate-img-wrapper {
  flex-shrink: 0;
}

// STM 스위치 관련 스타일
.switch-text-wrap {
  display: flex;
  align-items: center;
}

.surface-text-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.certificate-img-area {
  width: 100%;
  height: 350px;
  position: relative;
  overflow: visible;

  .certificate-img.full {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 12px;
    overflow: visible;

    img {
      width: 100% !important;
    }

    .stm-overlay-img {
      position: absolute;
      width: 220px;
      height: 400px;
      top: -10%;
      left: -10%;
      z-index: 3;
      object-fit: contain;
    }

    &.flip {
      transform: rotateY(180deg);
      transition: transform 0.6s;
    }

    .card-back {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transform: rotateY(180deg);
      backface-visibility: hidden;
    }
  }
}

.surface-container {
  margin-top: 20px;

  .surface-title-area {
    .surface-text-button {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .tooltip-label-wrap {
        display: flex;
        align-items: center;
        gap: 8px;

        .question-mark {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #fff;
          border: none;
          color: #acb2b9;
          border: 1px solid #acb2b9;
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .label-wrap {
          .label-text {
            font-size: 15px;
            color: #2f3337;
            font-weight: 500;
          }
        }
      }
    }

    .true-color {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
    }
  }
}

.companies-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.change-order-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #dde0e4;
  border-radius: 4px;
  color: #6e747c;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #f1f2f3;
  }
}

.is-certified-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .left-area {
    font-size: 14px;
    font-weight: 500;
    color: #4b5158;
  }
}

@media (max-width: 768px) {
  .teeth-item-wrap {
    flex-direction: column;
  }

  .certificate-img-area {
    height: 250px;
  }
}

// GTIN 코드 입력 관련 스타일
.gtin-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .gtin-error {
    margin-top: 8px;
    padding: 8px 12px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #dc2626;
    font-size: 14px;
  }
}

// 실물카드 검색 wrapper (SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 M2)
.card-search-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  // a-select 닫힌 상태 선택값(option-label-prop="label") — 1행 말줄임, caret/placeholder와 겹치지 않도록
  :deep(.ant-select-selection-item) {
    font-family: "SF Mono", Monaco, "Consolas", monospace;
    font-size: 13px;
  }

  // SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 — mode="multiple" 레이아웃 깨짐 수정.
  // 전역 ant-design.scss(.ant-select-selector { height:40px !important })가 모든 셀렉트에
  // 고정 높이를 강제해, 카드(태그)를 2개 이상 담으면 태그가 40px 박스 밖으로 넘쳐 아래
  // STM 행과 겹친다. 이 카드검색 멀티셀렉트에 한해서만 높이를 auto로 풀어, 선택 태그가
  // 늘면 박스가 함께 커지도록 한다(전역 규칙·단일 셀렉트·타 화면에는 영향 없음).
  :deep(.ant-select-multiple .ant-select-selector) {
    height: auto !important;
    min-height: 40px;
    align-items: flex-start;
    padding-top: 3px;
    padding-bottom: 3px;
  }
  // 선택 태그 목록은 줄바꿈으로 흘러 여러 줄을 차지한다(박스 높이가 이에 맞춰 커짐).
  :deep(.ant-select-multiple .ant-select-selection-overflow) {
    flex-wrap: wrap;
  }
}

// 카드 카운터 라인 — "카드 n/N장" (Wave2 REQ-ADM-MULTI-002). 선택된 카드는
// a-select mode="multiple"의 내장 태그(칩) 렌더링으로 표시되므로 별도 요약 패널은 두지 않는다.
.card-counter-line {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
}
</style>
<style scoped lang="scss">
@import "./css/issue-certificate.scss";
@import "./css/implant-abutment.scss";
</style>
